// node_modules
import { FC, useContext, useEffect, useState } from "react";
// Custom hooks
import { useFetch } from "Hooks";
// Stores
import { CaseContext, TechnologyContext } from "Stores";
// Types
import { TTechnologyPatentsDTO, TUseFetch } from "Types";
// Style
import styles from "../technologyDocuments.module.scss";
// Components
import { AsynchronousWrapper, DocumentHeaderSplitter, UsPatentDocument } from "Components";
// Enums
import { TechnologyActionTypeEnum } from "Enums";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Interfaces
import { ISetTechnologyCountsAction } from "Interfaces";
import { PendoHelperSingleton } from "Helpers";
import { LoggingConstants } from "Constants";

export const TechnologyPatentDocuments: FC = () => {
    // State
    const [patents, setPatents] = useState<TTechnologyPatentsDTO>();

    // Contexts
    const { caseState } = useContext(CaseContext);
    const { technologyState, technologyDispatch } = useContext(TechnologyContext);

    // Let
    const { isLoading: isPatentsLoading, didError: didPatentsError, errorMessage: patentsErrorMessage, fetchedData: fetchedPatents }: TUseFetch<TTechnologyPatentsDTO> = useFetch(`api/technologies/${caseState.selectedTechnologyId}/documents/patents`);

    // Logic
    useEffect(() => {
        if (!fetchedPatents) return;
        setPatents(fetchedPatents);
        
    }, [fetchedPatents]);

    // Logging
    useEffect(() => {
        // Log the fetching of the patents
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyGetPatents`, {
            technologyId: caseState.selectedTechnologyId
        });
    }, [caseState.selectedTechnologyId]);

    const updateTechnologyPatentsAsync = async (): Promise<void> => {
        if (!caseState.selectedTechnologyId) return;

        const newPatents: TTechnologyPatentsDTO = await TechnologyControllerSingleton.getTechnologyPatentDocumentsAsync(caseState.selectedTechnologyId);

        // update counts in the TechnologyContext (in order to re-render components which use this counts)
        technologyState.counts = await TechnologyControllerSingleton.getTechnologyCountsAsync(caseState.selectedTechnologyId);
        technologyDispatch({ type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: technologyState.counts } as ISetTechnologyCountsAction);

        setPatents(newPatents);
    };

    const handlePatentHasBeenUnbookmarked = async (unbookmarkOnEntityId: string): Promise<void> => {
        if (caseState.selectedOriginalTechnologyId &&
            caseState.selectedOriginalTechnologyId.toString() === unbookmarkOnEntityId) {
            await updateTechnologyPatentsAsync();
        }
    };


    return (
        <AsynchronousWrapper didError={didPatentsError} errorMessage={patentsErrorMessage} isLoading={isPatentsLoading} LoaderComponent={null}>
            <div className={styles.technologyDocumentsContentContainer}>
                <div>
                    {patents && patents.scoutPatents && patents.scoutPatents.length >= 1 ?
                        <DocumentHeaderSplitter title="Scout added documents" />
                        :
                        null
                    }
                    {patents?.scoutPatents.map((patentDto) => {
                        patentDto.isBookmarked = true;
                        return (
                            <UsPatentDocument key={patentDto.id} patentDto={patentDto} />
                        );
                    })}
                </div>
                {patents && patents.coworkerPatents.length > 0 ?
                    <div>
                        <DocumentHeaderSplitter title="Coworker added documents" />
                        {patents.coworkerPatents.map((patentDto) => {
                            patentDto.isBookmarked = true;
                            return (
                                <UsPatentDocument 
                                    key={patentDto.id} 
                                    patentDto={patentDto} 
                                    handlePatentHasBeenUnbookmarked={handlePatentHasBeenUnbookmarked} />
                            );
                        })}
                    </div> :
                    null}
                {patents && patents.currentUserPatents.length > 0 ?
                    <div>
                        <DocumentHeaderSplitter title="Your added documents" />
                        {patents.currentUserPatents.map((patentDto) => {
                            patentDto.isBookmarked = true;
                            return (
                                <UsPatentDocument 
                                    key={patentDto.id} 
                                    patentDto={patentDto} 
                                    handlePatentHasBeenUnbookmarked={handlePatentHasBeenUnbookmarked} />
                            );
                        })}
                    </div> :
                    null}
            </div>
        </AsynchronousWrapper>
    );
};